import React, { useState, InputHTMLAttributes, useEffect, ChangeEvent } from 'react';
import FormInput from './FormInput';

interface AutoSavingInputProps {
    label?: string;
    name: string;
    type: string;
    placeholder?: string;
    currency?: boolean;
    onChange?: React.ChangeEventHandler<HTMLInputElement> | undefined;
    className?: string;
    value: string | number;
    containerClass?: string;
    min?: number;
    max?: number;
    style?: React.CSSProperties;
    direction?: 'rtl' | 'ltr';
    onSave: (value: string) => Promise<void>;
}

/* Password Input */
const AutoSavingInput = ({
    type,
    name,
    containerClass,
    style,
    value,
    placeholder,
    onChange,
    className,
    direction = 'rtl',
    currency = false,
    onSave,
    ...otherProps
}: AutoSavingInputProps) => {
    const [inputValue, setInputValue] = useState<string | number>(value);
    const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const MIN_LOADING_TIME = 500;

    const defaultWrapperStyle: React.CSSProperties = {
        position: 'relative',
        display: 'inline-block',
    };

    const defaultSpinnerStyle: React.CSSProperties = {
        top: '22%',
        width: '20px',
        height: '20px',
    };

    const leftIconStyle: React.CSSProperties = {
        position: 'absolute',
        left: '10px',
        top: '50%',
        transform: 'translateY(-50%)',
    };

    useEffect(() => {
        // Clear the timeout when the component unmounts to prevent memory leaks
        return () => {
            if (typingTimeout) {
                clearTimeout(typingTimeout);
            }
        };
    }, [typingTimeout]);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setInputValue(value);

        // Clear the previous timeout if the user types again
        if (typingTimeout) {
            clearTimeout(typingTimeout);
        }

        // Set a new timeout to call onSave after 5 seconds
        const timeout = setTimeout(async () => {
            const t1 = new Date().getTime();
            setIsSaving(true);

            await onSave(value);
            const delta = new Date().getTime() - t1;
            const extraTime = delta < MIN_LOADING_TIME ? MIN_LOADING_TIME - delta : 0;
            setTimeout(() => setIsSaving(false), extraTime);
        }, 2500);

        setTypingTimeout(timeout);
    };

    return (
        <div className="wrapper" style={defaultWrapperStyle}>
            <div style={defaultWrapperStyle}>
                {!isSaving && currency && <span style={leftIconStyle}>₪</span>}
                {isSaving && <div className="spinner" style={{ ...leftIconStyle, ...defaultSpinnerStyle }} />}
                <FormInput
                    type={type}
                    name={name}
                    containerClass={containerClass}
                    placeholder={placeholder}
                    onChange={handleInputChange}
                    className={className}
                    value={inputValue}
                    style={style}
                    {...otherProps}
                />
            </div>
        </div>
    );
};

export default AutoSavingInput;
